import React, { createContext, useContext, useMemo } from 'react';
import { DataContext } from './DataContext';
import { useQuery } from '@tanstack/react-query';

export interface EDFDataContextType {
  startDate?: number;
  endDate?: number;
  freq?: number;
  lFreq?: number;
  hFreq?: number;
  limit?: number;
}

const EDFDataContext = createContext<EDFDataContextType | undefined>(undefined);

const fetchChannelData = async (
  signal: AbortSignal,
  channel: string,
  config: EDFDataContextType
) => {
  const formattedSearchParams = Object.entries(config)
    .map(([key, value]) => {
      const snakeCaseKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
      return value ? `&${snakeCaseKey}=${value}` : "";
    })
    .join("");
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}edf/seal/channels/${channel}?${formattedSearchParams}`,
    { signal }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

export const useChannelData = () => {
  const { selectedChannel, window } = useContext(DataContext);
  const searchParams = useEDFDataParams();

  return useQuery({
    queryKey: ["channelData", selectedChannel, ...Object.values(searchParams)],
    queryFn: ({ signal }) => fetchChannelData(signal, selectedChannel, searchParams),
    enabled: !!selectedChannel && !!window?.length,
    staleTime: 5000,
  });
};

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;
const EDF_CHANNELS = [/ecg/i, /eog/i, /eeg/i];

export const EDFDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedChannel, window } = useContext(DataContext);
  const isEdf = useMemo(() => EDF_CHANNELS.some(regex => regex.test(selectedChannel)), [selectedChannel]);
  const startDate = new Date(window[0]).getTime();
  const endDate = new Date(window[1]).getTime();
  const tsDiff = endDate - startDate;

  const isCoarse = tsDiff >= THREE_HOURS_MS;
  const freq = isCoarse ? 1 : 101;
  const hFreq = 0.1;
  let lFreq: number;
  let limit = 10000;
  if (!isCoarse) {
    lFreq = selectedChannel?.includes("ECG")
      ? 50
      : 35;
    limit = limit * 5;
  }

  const searchParams: EDFDataContextType = {
    startDate,
    endDate,
  };

  if (isEdf) {
    searchParams.freq = freq;
    searchParams.lFreq = hFreq;
    searchParams.hFreq = lFreq;
    searchParams.limit = limit;
  }

  return (
    <EDFDataContext.Provider value={searchParams}>
      {children}
    </EDFDataContext.Provider>
  );
};

export const useEDFDataParams = (): EDFDataContextType => {
  const context = useContext(EDFDataContext);
  if (context === undefined) {
    throw new Error('useEDFData must be used within a EDFDataProvider');
  }
  return context;
};

